import { createContext, useContext, useReducer, useState } from 'react';

import { useLazyQuery, useMutation, useQuery } from '@apollo/client';

import {
	GET_CATEGORY,
	GET_CATEGORIES,
	CREATE_CATEGORY,
	UPDATE_CATEGORY,
	DELETE_CATEGORY,
	GET_CATEGORIES_PAGINATE
} from 'graphql/categories';
import { ACTION_TYPES, reducers } from './reducers';

const CategoriesStateContext = createContext();
const CategoriesDispatchContext = createContext();

const initialState = {
	rows: [],
	page: 1,
	perPage: 15,
	categories: {}
};

const CategoriesProvider = ({ children }) => {
	const [state, dispatch] = useReducer(reducers, initialState);
	const [toDelete, setToDelete] = useState(null);
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);

	return (
		<CategoriesStateContext.Provider
			value={{
				...state,
				toDelete,
				setToDelete,
				deleteModalOpen,
				setDeleteModalOpen
			}}
		>
			<CategoriesDispatchContext.Provider value={dispatch}>
				{children}
			</CategoriesDispatchContext.Provider>
		</CategoriesStateContext.Provider>
	);
};

const useCategories = () => {
	const context = useContext(CategoriesStateContext);

	if (!context)
		throw new Error(
			'useCategories must be used within an CategoriesProvider'
		);

	return context;
};

const useDispatch = () => {
	const dispatch = useContext(CategoriesDispatchContext);

	if (dispatch === undefined)
		throw new Error('useDispatch must be used within a CategoriesProvider');

	return dispatch;
};

const useAllCategories = () => {
	const dispatch = useDispatch();

	return useQuery(GET_CATEGORIES, {
		fetchPolicy: 'no-cache',
		onCompleted: (data) => {
			dispatch({ type: ACTION_TYPES.ALL, data: data.categories });
		}
	});
};

const usePaginateCategories = () => {
	const dispatch = useDispatch();

	return useLazyQuery(GET_CATEGORIES_PAGINATE, {
		onCompleted: (data) => {
			window.history.replaceState(
				null,
				'',
				`./categorias?page=${data.paginateCategories.paginatorInfo.currentPage}&size=${data.paginateCategories.paginatorInfo.perPage}`
			);
			dispatch({
				type: ACTION_TYPES.LIST,
				data: data.paginateCategories
			});
		}
	});
};

const useCreateCategory = (options = {}) => {
	const dispatch = useDispatch();
	const { onSuccess, onError } = options;

	return useMutation(CREATE_CATEGORY, {
		onCompleted: (data) => {
			if (onSuccess) onSuccess(data);
			dispatch({ type: ACTION_TYPES.ADD, data: data.createCategory });
		},
		onError: (error) => {
			if (onError) onError(error);
		}
	});
};

const useCategory = (id) => {
	const dispatch = useContext(CategoriesDispatchContext);

	return useQuery(GET_CATEGORY, {
		fetchPolicy: 'no-cache',
		variables: {
			id
		},
		onCompleted: (data) =>
			dispatch({ type: ACTION_TYPES.VIEW, data: data.getCategory })
	});
};

const useUpdateCategory = (options = {}) => {
	const dispatch = useContext(CategoriesDispatchContext);
	const { onSuccess, onError } = options;

	return useMutation(UPDATE_CATEGORY, {
		onCompleted: (data) => {
			if (onSuccess) onSuccess(data);
			dispatch({ type: ACTION_TYPES.UPDATE, data: data.updateCategory });
		},
		onError: (error) => {
			if (onError) onError(error);
		},
		refetchQueries: [
			{
				query: GET_CATEGORIES_PAGINATE
			}
		]
	});
};

const useDeleteCategory = (options = {}) => {
	const dispatch = useDispatch();
	const { onSuccess } = options;

	return useMutation(DELETE_CATEGORY, {
		onCompleted: (data) => {
			if (onSuccess) onSuccess();
			dispatch({
				type: ACTION_TYPES.REMOVE,
				id: data.deleteCategory.id
			});
		}
	});
};

export {
	CategoriesProvider,
	useCategories,
	useAllCategories,
	useCreateCategory,
	usePaginateCategories,
	useCategory,
	useUpdateCategory,
	useDeleteCategory
};
